/**
 * The words the turn-outcome banners use — a plain module with no React and no
 * UI import, so a SERVER test can hold it against the server's own sentences
 * (`turnBudgetNotice()`, `stepCapNotice()`, `wallClockNotice()`) and fail when
 * the banner and the appended text stop agreeing about why an answer ended.
 *
 * Operator words only. No stop-reason code, no diagnostic, no raw tool name —
 * the raw name rides on the notice for attribution and is never read here. The
 * one string taken off the notice is `label`, the server humanLabel, which is
 * the same text the activity strip already showed for that tool while it ran.
 */
import type { TurnNotice, TurnNoticeKind } from "@/store/api/crmSkyAgentApi";

export type TurnNoticeIntent = "info" | "warning" | "error";

export interface TurnNoticeCopy {
  intent: TurnNoticeIntent;
  text: string;
}

/**
 * The phrases each banner SHARES with the sentence the server appends to the
 * answer for the same stop reason. Exported so the parity test asserts both
 * sides against the same literal rather than two copies of it.
 */
export const SHARED_STOP_PHRASES = {
  budget: "stopped at the limit this workspace sets for a single question",
  daily_budget: "stopped because this workspace does not have enough of its daily AI budget left",
  event_cap: "stopped at the step limit",
  wall_clock: "stopped at the time limit",
} as const;

const NARROW_IT =
  "Try a narrower question — one community, a shorter date range, or one thing at a time.";

/** Quote the tool's label into a sentence, or nothing when there is none. */
function about(label: string | undefined): string {
  return label ? ` (“${label}”)` : "";
}

/**
 * Budget, the caps and a refused repeat are WARNINGS because the answer on
 * screen may be short of what was asked; a timeout and a served repeat are
 * INFO because the answer is still what the model intended, and the reader
 * only needs to know why it took the shape it did.
 *
 * The cap and clock lines name no Settings control: unlike the token ceiling
 * those are deploy settings, and the bar must not send a counselor looking
 * for one.
 */
export const TURN_NOTICE_COPY: Record<TurnNoticeKind, (notice: TurnNotice) => TurnNoticeCopy> = {
  budget: (n) => ({
    intent: "warning",
    text:
      n.scope === "daily"
        ? `This answer ${SHARED_STOP_PHRASES.daily_budget}. What it found stands — the ` +
          "budget resets at midnight UTC, or an administrator can raise it under " +
          "Settings → AI Governance."
        : `This answer ${SHARED_STOP_PHRASES.budget}. ` +
          "What it found stands — ask a narrower question to continue, or an administrator " +
          "can raise the limit under Settings → AI Governance.",
  }),
  event_cap: () => ({
    intent: "warning",
    text:
      `This answer ${SHARED_STOP_PHRASES.event_cap} for a single question. What it found ` +
      `stands — ask a narrower follow-up to continue. ${NARROW_IT}`,
  }),
  wall_clock: () => ({
    intent: "warning",
    text:
      `This answer ${SHARED_STOP_PHRASES.wall_clock} for a single question. What it found ` +
      `stands — ask a narrower follow-up to continue. ${NARROW_IT}`,
  }),
  tool_timed_out: (n) => ({
    intent: "info",
    text:
      `One lookup${about(n.label)} took too long and was stopped, so part of this answer ` +
      `may be missing. ${NARROW_IT}`,
  }),
  repeat_served: (n) => ({
    intent: "info",
    text:
      `skyAgent asked for the same information twice${about(n.label)}; the repeat was ` +
      "answered from the earlier identical lookup rather than read again.",
  }),
  repeat_refused: (n) => ({
    intent: "warning",
    text:
      `skyAgent tried to repeat a lookup${about(n.label)} that had already failed this ` +
      "turn, and the repeat was refused. Part of this answer may be missing.",
  }),
  error: () => ({
    intent: "error",
    text: "skyAgent could not finish this answer. Please try again.",
  }),
};
